// Web variant of `geolocation.ts`. The native app asks Maplibre's native
// LocationManager for the user's position; the web build uses the browser
// Geolocation API instead. The exported API mirrors the native file.
export type Coordinates = {
  latitude: number;
  longitude: number;
};

type BrowserGeolocation = {
  geolocation: {
    getCurrentPosition(
      onSuccess: (position: { coords: { latitude: number; longitude: number } }) => void,
      onError?: (error: { code?: number; message?: string }) => void,
    ): void;
  };
  permissions?: {
    query?: (descriptor: { name: 'geolocation' }) => Promise<{ state: string }>;
  };
};

const getBrowserGeolocation = (): BrowserGeolocation['geolocation'] | null => {
  const navigatorRef = (
    globalThis as { navigator?: BrowserGeolocation }
  ).navigator;

  return navigatorRef?.geolocation ?? null;
};

const getPermissionQuery = (): ((
  descriptor: { name: 'geolocation' },
) => Promise<{ state: string }>) | null => {
  const navigatorRef = (
    globalThis as { navigator?: BrowserGeolocation }
  ).navigator;

  return navigatorRef?.permissions?.query ?? null;
};

const getCurrentBrowserPosition = (): Promise<{
  coords: { latitude: number; longitude: number };
} | null> =>
  new Promise(resolve => {
    const geolocation = getBrowserGeolocation();

    if (!geolocation) {
      resolve(null);
      return;
    }

    geolocation.getCurrentPosition(
      position => resolve(position),
      () => resolve(null), // Denied or unavailable -> behave like "no permission".
    );
  });

// The browser shows its permission prompt during `getCurrentPosition`, so a
// non-"denied" state is enough here.
export const requestLocationPermission = async (): Promise<boolean> => {
  if (!getBrowserGeolocation()) {
    return false;
  }

  const query = getPermissionQuery();

  if (!query) {
    return true;
  }

  try {
    const permission = await query({ name: 'geolocation' });

    return permission.state !== 'denied';
  } catch {
    return true;
  }
};

export const getCurrentPosition = async (): Promise<Coordinates | null> => {
  const position = await getCurrentBrowserPosition();

  if (!position) {
    return null;
  }

  return getCoordinates(position);
};

export const getCoordinates = (position: {
  coords: { latitude: number; longitude: number };
}): Coordinates => ({
  latitude: position.coords.latitude,
  longitude: position.coords.longitude,
});

export const getUserCoordinates = async (): Promise<Coordinates | null> => {
  const hasPermission = await requestLocationPermission();

  if (!hasPermission) {
    return null;
  }

  try {
    return await getCurrentPosition();
  } catch (error) {
    console.error('Failed to retrieve user location', error);

    return null;
  }
};
